/**
 * Email (design: Gmail-style one-way mail from Caryn). Mail comes from Caryn's server (for now one Welcome email):
 * opening it is recorded, "Mark as read" is recorded, and it deletes 24 hours after the first open ("Deletes in …").
 * "Reply" is honest: replies go to her texts (Text), the only place a fan can write to her.
 */
import { useEffect, useMemo, useState } from 'react'
// (useNavigate + usePin come back with the hidden buttons below)
import { useAuth } from '@/features/auth'
import { inbox, type Mail } from '~/api/endpoints'
import { Icon, longTime, shortTime, useToast } from '~/app/ui'

export function Inbox() {
  const { user } = useAuth()
  const [mails, setMails] = useState<Mail[] | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const toast = useToast()
  const first = (user as any)?.firstName || (user as any)?.first_name || 'there'
  const toName = [(user as any)?.firstName || (user as any)?.first_name, (user as any)?.lastName || (user as any)?.last_name].filter(Boolean).join(' ') || 'me'
  const [now, setNow] = useState(Date.now())
  useEffect(() => { inbox.list().then(setMails).catch(() => setMails([])) }, [])
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t) }, [])
  const put = (m: Mail) => setMails((all) => all?.map((x) => (x.id === m.id ? m : x)) || null)
  const shown = useMemo(() => (mails || []).filter((m) => !q.trim() || `${m.subject} ${m.preview} ${m.body.join(' ')}`.toLowerCase().includes(q.trim().toLowerCase())), [mails, q])

  const toggle = async (m: Mail) => {
    const opening = open !== m.id
    setOpen(opening ? m.id : null)
    if (!opening) return
    const next = await inbox.open(m) // recorded on the server: first/last open + count
    if (next === null) { setMails((all) => all?.filter((x) => x.id !== m.id) || null); toast('That email was deleted'); return }
    put(next)
    // owner 11-04: opening it marks it read INSTANTLY (shown at once, saved on the server too); no "mark as unread"
    if (!next.readAt) {
      put({ ...next, readAt: new Date().toISOString(), read: true })
      try { put(await inbox.markRead(next, true)) } catch { /* the open is recorded; read is shown */ }
    }
  }
  /** "Deletes in 23 h 59 min" after the first open; before that, how long it lasts once opened */
  const deletes = (m: Mail) => {
    if (m.deletesAt) {
      const ms = Date.parse(m.deletesAt) - Math.max(now, Date.now())
      if (ms <= 0) return 'Deleting now'
      const h = Math.floor(ms / 3600e3), min = Math.max(1, Math.floor((ms % 3600e3) / 60e3))
      return `Deletes in ${h ? `${h} h ` : ''}${min} min`
    }
    return m.deleteHoursAfterOpen ? `Deletes ${m.deleteHoursAfterOpen} hours after you open it` : ''
  }

  return (
    <main className="wrap inbox-view" id="main">
      <section className="inbox-head" aria-labelledby="inboxTitle">
        <p className="eyebrow"><Icon id="i-lock" /> Confidential · messages from Caryn</p>
        <h1 id="inboxTitle">Email</h1>
      </section>
      <div className="gm">
        <div className="gm-bar"><label className="gm-search"><Icon id="i-search" /><input type="search" placeholder="Search mail" aria-label="Search mail" value={q} onChange={(e) => setQ(e.target.value)} /></label></div>
        {mails === null ? <p className="v2-muted" role="status" style={{ padding: 20 }}>Loading…</p>
          : shown.length === 0 ? <p className="v2-muted" style={{ padding: 20 }}>{q ? 'Nothing matches that search.' : 'No messages yet. Caryn’s notes will land here.'}</p>
          : (
            <ul className="gm-list" aria-label="Messages">
              {shown.map((m) => (
                <li key={m.id} className={`gm-item${m.readAt ? '' : ' unread'}`}>
                  <button className="gm-row" type="button" aria-expanded={open === m.id} onClick={() => toggle(m)}>
                    <span className="gm-check" aria-hidden="true"><i /></span>
                    <span className="gm-from"><span className="gm-dot" aria-hidden="true" /><span className="gm-name">{m.from}</span></span>
                    <span className="gm-text"><span className="gm-subj">{m.subject}</span>{m.preview && <span className="gm-prev"> – {m.preview}</span>}</span>
                    <span className="gm-date">{m.deletesAt ? <span className="v2-deletes"><Icon id="i-clock" />{deletes(m).replace('Deletes in ', '')}</span> : shortTime(m.at)}</span>
                  </button>
                  <div className="gm-thread" hidden={open !== m.id}>
                    <h2>{m.subject}</h2>
                    <div className="gm-who">
                      <div className="avatar" aria-hidden="true"><svg className="icon text-ic" viewBox="0 0 24 24" aria-hidden="true"><path d="M4.5 4.5h15a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-7.5l-4.5 3.8v-3.8h-3a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2z" /><path className="bubble-heart" d="M12 15.19s-4.2-2.64-4.2-6a2.4 2.4 0 0 1 4.2-1.56A2.4 2.4 0 0 1 16.2 9.19c0 3.36-4.2 6-4.2 6z" /></svg></div>
                      <div><b>{m.from}</b><small>to {toName}</small></div>
                      <span className="gm-when">{longTime(m.at)}</span>
                    </div>
                    <div className="v2-mail-state">
                      {deletes(m) && <span className="v2-deletes"><Icon id="i-clock" />{deletes(m)}</span>}
                      {m.readAt && <span className="v2-markread v2-is-read"><Icon id="i-check" />Read</span>}
                    </div>
                    <div className="gm-body">
                      {m.body.map((t, i) => <p key={i}>{t.replace('{name}', first)}</p>)}
                      {m.photo && <div className="v2-mail-photo"><img src={m.photo} alt="" loading="lazy" /></div>}
                      <p className="email-sign">Caryn</p>
                    </div>
                    {/* owner 11-04: buttons under the mail hidden for now ("I might add it back") - Say hi / See the gallery / Reply in our texts
                    <div className="gm-links">
                      {m.links.map(([to, label], i) => <button key={to + i} className={`btn ${i ? 'btn-quiet' : 'btn-light'}`} type="button" onClick={() => follow(to)}>{label}</button>)}
                    </div>
                    <div className="gm-actions"><button type="button" onClick={() => go('/text')}><Icon id="i-reply" />Reply in our texts</button></div>
                    */}
                  </div>
                </li>
              ))}
            </ul>
          )}
      </div>
    </main>
  )
}
